import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  Tooltip,
} from "recharts";
import { SCENARIOS, KPIS, MARKET, MARKET_CONTEXT, IMAGES, COMPANY } from "@/data/content";
import { SectionLabel, Reveal } from "@/components/site/primitives";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      data-testid="chart-tooltip"
      className="border border-epic-lineHover bg-epic-surface px-4 py-3"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-epic-sub">{label}</p>
      <p className="font-grotesk font-bold text-xl text-epic-accent">${payload[0].value}M</p>
    </div>
  );
};

export const Financials = () => {
  const [active, setActive] = useState("base");
  const scenario = SCENARIOS[active];
  const order = ["downside", "base", "upside"];

  return (
    <section
      id="financials"
      data-testid="financials-section"
      className="mx-auto max-w-[1400px] px-5 md:px-10 py-28 md:py-44"
    >
      <SectionLabel index="[ 03 ]" testId="financials-label">
        The Financial Model
      </SectionLabel>

      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-20">
          <h2 className="font-grotesk font-black uppercase tracking-tighter text-4xl md:text-6xl lg:text-7xl text-epic-text leading-[0.9] max-w-3xl">
            Five-year<br />
            <span className="text-epic-accent">revenue</span> build.
          </h2>
          {/* Scenario toggle */}
          <div className="flex border border-epic-line" data-testid="scenario-toggle">
            {order.map((k) => (
              <button
                key={k}
                type="button"
                data-testid={`scenario-${k}`}
                onClick={() => setActive(k)}
                className={`px-4 md:px-6 py-3 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                  active === k ? "bg-epic-accent text-white" : "text-epic-sub hover:text-epic-text"
                }`}
              >
                {SCENARIOS[k].label}
              </button>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Bento grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
        {/* Revenue chart */}
        <Reveal className="md:col-span-8 border border-epic-line bg-epic-surface p-6 md:p-10">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-sub mb-2">
                {scenario.label} Case — Total Revenue
              </p>
              <p className="font-grotesk font-bold text-2xl md:text-3xl text-epic-text">
                Year 5: {scenario.y5rev}
              </p>
            </div>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-accent">
              EBITDA {scenario.y5ebitda}
            </p>
          </div>
          <div className="h-[280px] md:h-[340px] w-full" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={scenario.data} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF3B30" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#FF3B30" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="year"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#888888", fontFamily: "JetBrains Mono", fontSize: 11 }}
                  dy={10}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#333333", strokeWidth: 1 }} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#FF3B30"
                  strokeWidth={2.5}
                  fill="url(#rev)"
                  dot={{ fill: "#FF3B30", r: 3 }}
                  activeDot={{ r: 6, fill: "#FF3B30", stroke: "#030303", strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Reveal>

        {/* Implied equity value */}
        <Reveal delay={0.1} className="md:col-span-4 border border-epic-line bg-epic-accent p-6 md:p-10 flex flex-col justify-between">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/70">
            Implied Equity Value ({scenario.label})
          </p>
          <div className="mt-8">
            <p className="font-grotesk font-black text-6xl md:text-7xl text-white leading-none tracking-tighter">
              {scenario.equity}
            </p>
            <p className="font-mono text-xs text-white/80 mt-3">
              Year-5 EBITDA {scenario.y5ebitda} × {scenario.multiple} exit multiple
            </p>
          </div>
          <div className="mt-8 pt-6 border-t border-white/20 space-y-3 font-mono text-xs text-white">
            <div className="flex justify-between"><span className="text-white/70">Y5 Revenue</span><span>{scenario.y5rev}</span></div>
            <div className="flex justify-between"><span className="text-white/70">Exit Multiple</span><span>{scenario.multiple}</span></div>
          </div>
        </Reveal>

        {/* KPIs */}
        {KPIS.map((kpi, i) => (
          <Reveal
            key={kpi.label}
            delay={i * 0.05}
            className="md:col-span-4 border border-epic-line bg-epic-surface p-6 md:p-8 transition-colors duration-300 hover:border-epic-lineHover"
          >
            <p className="font-grotesk font-black text-4xl md:text-5xl text-epic-text tracking-tighter">
              {kpi.value}
            </p>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-sub mt-3">
              {kpi.label}
            </p>
          </Reveal>
        ))}

        {/* Market size */}
        <Reveal className="md:col-span-7 border border-epic-line bg-epic-surface p-6 md:p-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-sub mb-8">
            Market Size
          </p>
          <div className="space-y-6">
            {MARKET.map((m, i) => (
              <div key={m.label} className="flex items-baseline justify-between gap-4 border-b border-epic-line pb-6 last:border-0 last:pb-0">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-sub">{m.label}</p>
                  <p className="font-mono text-[11px] text-epic-sub/60 mt-1">{m.note}</p>
                </div>
                <p className="font-grotesk font-black tracking-tighter text-epic-text text-3xl md:text-5xl">
                  {m.value}
                </p>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Market context + image */}
        <Reveal delay={0.1} className="md:col-span-5 border border-epic-line overflow-hidden relative min-h-[320px] flex flex-col justify-end">
          <img
            src={IMAGES.building}
            alt="The great wealth transfer"
            className="absolute inset-0 w-full h-full object-cover grayscale contrast-125 opacity-40 transition-transform duration-700 hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-epic-bg via-epic-bg/70 to-transparent" />
          <div className="relative p-6 md:p-8 space-y-5">
            {MARKET_CONTEXT.map((c) => (
              <div key={c.value} className="flex items-baseline gap-4">
                <span className="font-grotesk font-black text-3xl md:text-4xl text-epic-accent tracking-tighter shrink-0 w-24">
                  {c.value}
                </span>
                <span className="font-mono text-xs text-epic-text leading-relaxed">{c.label}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.1} className="mt-8">
        <a
          href={COMPANY.modelUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="financials-view-source"
          className="group inline-flex items-center gap-3 bg-epic-accent px-8 py-5 text-white font-mono text-xs uppercase tracking-[0.15em] transition-[background-color,transform] duration-300 hover:bg-epic-accentHover active:scale-95"
        >
          See Where the Numbers Come From
          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
        <p className="font-mono text-[11px] text-epic-sub/60 mt-3">
          Opens the full working financial model — every assumption and formula behind these figures.
        </p>
      </Reveal>
    </section>
  );
};
